import React from "react";
import RecordManager from "../../../components/founder/RecordManager";
import AssessmentReference from "../../../components/founder/AssessmentReference";
import api from "../../../lib/api";
import { toast } from "sonner";
import { Target } from "lucide-react";

export default function DiagnosisTab({ ws, bid, reload }) {
  const setCore = async (c) => {
    try {
      await api.post(`/businesses/${bid}/constraints/${c.id}/core`, { core: !c.is_core });
      toast.success(c.is_core ? "Core Constraint removed" : "Set as Core Constraint");
      reload();
    } catch { toast.error("Could not update Core Constraint"); }
  };

  const coreAction = (c) => (
    <button onClick={() => setCore(c)} data-testid={`constraint-core-${c.is_core ? "on" : "off"}`}
      title={c.is_core ? "Remove Core Constraint designation" : "Set as Core Constraint"}
      className={`p-1.5 rounded-[4px] ${c.is_core ? "text-lr-olive" : "text-lr-leather hover:text-lr-olive"}`}>
      <Target className="h-4 w-4" fill={c.is_core ? "currentColor" : "none"} />
    </button>
  );

  const interventions = [...(ws.interventions || [])].sort((a, b) => {
    const ao = a.sort_order ?? 1e9, bo = b.sort_order ?? 1e9;
    if (ao !== bo) return ao - bo;
    return (a.created_at || "").localeCompare(b.created_at || "");
  });

  const reorderInterventions = async (idx, dir) => {
    const arr = [...interventions];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try {
      await api.post(`/businesses/${bid}/interventions/reorder`, { ids: arr.map((x) => x.id) });
      reload();
    } catch { toast.error("Could not reorder"); }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <AssessmentReference ws={ws} />
      <div className="lr-card p-5">
        <RecordManager
          resource="constraints" bid={bid} items={ws.constraints} testid="constraints-manager"
          title="Constraints" addLabel="Add constraint" onChange={reload}
          includeToggle includeReady={(c) => c.status === "Confirmed"}
          itemActions={coreAction}
          fields={[
            { name: "name", label: "Name", type: "text" },
            { name: "type", label: "Type", type: "text" },
            { name: "status", label: "Status", type: "select", options: ["Candidate", "Confirmed", "Rejected", "Resolved"] },
            { name: "confidence", label: "Confidence", type: "select", options: ["High", "Medium", "Low"] },
            { name: "business_impact", label: "Business impact", type: "text" },
            { name: "urgency", label: "Urgency", type: "text" },
            { name: "owner_awareness", label: "Owner awareness", type: "select", options: ["Known", "Partially Known", "Unknown"] },
            { name: "obviousness", label: "Obviousness", type: "select", options: ["Obvious to owner", "Likely known", "Potentially unknown", "Non obvious"] },
            { name: "upstream_causes", label: "Upstream causes", type: "textarea", rows: 2 },
            { name: "downstream_effects", label: "Downstream effects", type: "textarea", rows: 2 },
            { name: "likely_next_constraint", label: "Likely next constraint", type: "text" },
          ]}
          renderItem={(c) => (
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-lr-charcoal">{c.name}</span>
                <span className="text-xs px-1.5 py-0.5 rounded-[3px] bg-lr-clay/15 text-lr-clay">{c.status}</span>
                {c.is_core && <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-lr-olive text-lr-cream font-semibold tracking-wide" data-testid="constraint-core-badge">CORE CONSTRAINT</span>}
                {c.source === "Investigation Brief" && <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-lr-leather/12 text-lr-leather">from assessment</span>}
                {c.confidence && <span className="text-xs text-lr-leather">{c.confidence} confidence</span>}
                {c.obviousness === "Obvious to owner" && <span className="text-xs text-[#8f4a49]">⚑ obvious to owner</span>}
              </div>
              {c.type && <div className="text-xs text-lr-leather">{c.type}</div>}
              {c.downstream_effects && <div className="text-sm text-lr-charcoal">→ {c.downstream_effects}</div>}
            </div>
          )}
        />
        <p className="text-xs text-lr-leather mt-3">If a constraint is obvious &amp; already known, investigate <em>why it persists</em>, do not just name it.</p>
      </div>

      <div className="lr-card p-5">
        <RecordManager
          resource="contradictions" bid={bid} items={ws.contradictions} testid="contradictions-manager"
          title="Contradictions" addLabel="Add contradiction" onChange={reload}
          includeToggle includeReady={(c) => c.status === "Resolved"}
          fields={[
            { name: "name", label: "Contradiction", type: "text" },
            { name: "investigation_question", label: "Investigation question", type: "textarea", rows: 3 },
            { name: "status", label: "Status", type: "select", options: ["Open", "Resolved"] },
            { name: "resolution", label: "Resolution", type: "textarea", rows: 2 },
          ]}
          renderItem={(c) => (
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-lr-charcoal">{c.name}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-[3px] ${c.status === "Resolved" ? "bg-lr-olive/15 text-lr-olive" : "bg-lr-rose/15 text-[#8f4a49]"}`}>{c.status || "Open"}</span>
                {c.source && <span className="text-xs text-lr-leather">{c.source}</span>}
              </div>
              {c.investigation_question && <div className="text-sm text-lr-charcoal italic">{c.investigation_question}</div>}
              {c.resolution && <div className="text-sm text-lr-olive">✓ {c.resolution}</div>}
            </div>
          )}
        />
      </div>

      <div className="lr-card p-5">
        <RecordManager
          resource="assets" bid={bid} items={ws.assets} testid="assets-manager"
          title="Underused Assets" addLabel="Add asset" onChange={reload}
          includeToggle
          fields={[
            { name: "description", label: "Asset description", type: "text" },
            { name: "current_use", label: "Current use", type: "textarea", rows: 2 },
            { name: "potential_use", label: "Potential use", type: "textarea", rows: 2 },
            { name: "evidence", label: "Evidence", type: "text" },
            { name: "opportunity", label: "Opportunity", type: "textarea", rows: 2 },
          ]}
        />
      </div>

      <div className="lr-card p-5">
        <RecordManager
          resource="leave_alone" bid={bid} items={ws.leave_alone} testid="leave-alone-manager"
          title="Leave Alone Register" addLabel="Mark leave-alone" onChange={reload}
          includeToggle
          fields={[
            { name: "name", label: "What it is", type: "text" },
            { name: "reason", label: "Why it should remain", type: "textarea", rows: 2 },
            { name: "evidence", label: "Evidence", type: "text" },
            { name: "revisit_trigger", label: "What would cause it to be revisited", type: "textarea", rows: 2 },
          ]}
        />
        <p className="text-xs text-lr-leather mt-3">Protect functioning systems even when they look old-fashioned.</p>
      </div>

      <div className="lr-card p-5">
        <RecordManager
          resource="interventions" bid={bid} items={interventions} testid="interventions-manager"
          title="Interventions" addLabel="Add intervention" onChange={reload}
          includeToggle includeReady={(i) => i.status === "Approved"}
          reorderable onReorder={reorderInterventions}
          fields={[
            { name: "description", label: "Recommendation", type: "textarea", rows: 2 },
            { name: "priority", label: "Priority", type: "select", options: ["NOW", "NEXT", "LATER", "LEAVE ALONE", "PREPARE NEXT"] },
            { name: "why_it_matters", label: "Why this matters", type: "textarea", rows: 2 },
            { name: "expected_benefit", label: "Expected benefit (only if supportable)", type: "text" },
            { name: "impact", label: "Impact", type: "text" },
            { name: "status", label: "Status", type: "select", options: ["Proposed", "Hold", "Investigate Further", "Approved", "Rejected"] },
            { name: "reasoning", label: "Evidence / reasoning", type: "textarea", rows: 2, group: "internal" },
            { name: "urgency", label: "Urgency", type: "text", group: "internal" },
            { name: "difficulty", label: "Effort / difficulty", type: "text", group: "internal" },
            { name: "cost", label: "Cost or likely range", type: "text", group: "internal" },
            { name: "time_to_value", label: "Time to value", type: "text", group: "internal" },
            { name: "dependencies", label: "Dependencies", type: "text", group: "internal" },
            { name: "risk_tradeoff", label: "Risks / tradeoffs", type: "textarea", rows: 2, group: "internal" },
            { name: "confidence", label: "Confidence", type: "select", options: ["High", "Medium", "Low"], group: "internal" },
            { name: "owner_fit", label: "Owner fit", type: "text", group: "internal" },
            { name: "long_range_role", label: "How to approach it / Long Range role", type: "text", group: "internal" },
          ]}
          renderItem={(i) => (
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs px-1.5 py-0.5 rounded-[3px] bg-lr-olive text-lr-cream font-medium">{i.priority || "None"}</span>
                <span className="font-medium text-lr-charcoal">{i.description}</span>
              </div>
              <div className="text-xs text-lr-leather">{[i.impact && `Impact: ${i.impact}`, i.cost && `Cost: ${i.cost}`, i.status].filter(Boolean).join(" · ")}</div>
            </div>
          )}
        />
        <p className="text-xs text-lr-leather mt-3">Every intervention should trace back to a real constraint or opportunity. Use the arrows to set the order the customer reads them (What I Would Fix First and NOW / NEXT / LATER follow this order).</p>
      </div>
    </div>
  );
}
